import React, { useCallback, useEffect, useState } from 'react';
import {
    Button,
    Grid,
    Typography,
    useTheme,
} from '@mui/material';
import * as CustomerApi from '../../Services/CustomerApi';
import { Alert, Container } from 'react-bootstrap';
import { PagePanel, PageHeading } from 'Components/Styled';
import { siteTheme } from 'Components/SiteTheme';
import { Col, Row, Spinner } from 'react-bootstrap';

const formatAnswerForUserResponse = (question) => {
    switch (question?.type) {
        case 'multiple_choice': {
            let sanitizedAnswer;
            if (Array.isArray(question.answer)) {
                sanitizedAnswer = [...question.answer]
            } else if (typeof question.answer === "string") {
                sanitizedAnswer = [question.answer];
            }
            if (sanitizedAnswer) {
                if (question?.configurationData?.options) {
                    return sanitizedAnswer.reduce((acc, answer, index) => {
                        const option = question.configurationData.options.find(({ id }) => (answer === id)) || {};
                        acc += `${index !== 0 ? ', ' : ''}${option.label || answer}`;
                        return acc;
                    }, '');
                } else {
                    return sanitizedAnswer.join(',');
                }
            } else {
                return JSON.stringify(question.answer || '');
            }
        }
        case 'short_text':
        case 'text_area': {
            let sanitizedAnswer;
            if (Array.isArray(question.answer)) {
                sanitizedAnswer = [...question.answer]
            } else if (typeof question.answer === "string") {
                sanitizedAnswer = [question.answer];
            }
            if (sanitizedAnswer) {
                return sanitizedAnswer.join(',');
            } else {
                return JSON.stringify(question.answer || '');
            }
        }
        case 'custom': {
            switch (question?.configurationData?.questionCode) {
                case 'attendee_information': {
                    return question.answer.reduce((acc, attendee, index) => {
                        acc += `${index !== 0 ? ', ' : ''}${attendee.name}(${attendee.relation})`;
                        return acc;
                    }, '')
                }
                case 'secret_challenge':
                case 'weekly_challenge': {
                    return question.answer;
                }
                default: {
                    return JSON.stringify(question.answer || '');
                }
            }
        }
        default: {
            return JSON.stringify(question.answer || '');
        }
    }
}

const UserFormReponses = ({ customerId }) => {

    const [userDetails, setUserDetails] = useState(null);
    const [unSyncedExpDetails, setUnSyncedExpDetails] = useState([]);
    const [syncExpApiDetails, setSyncExpApiDetails] = useState({});

    const fetchUserDetails = useCallback(async () => {
        const res = await CustomerApi.getUserFormReponse(customerId, 'USER_DETAILS');
        setUserDetails(res?.[0] || null);
    }, [customerId])

    const fetchUserUnSyncedExpDetails = useCallback(async () => {
        const res = await CustomerApi.getUserUnSyncedFormReponse(customerId, 'EXPERIENCE_DETAILS');
        setUnSyncedExpDetails(res || []);
    }, [customerId]);

    const syncExpDetails = useCallback(async (formId) => {
        setSyncExpApiDetails((prevData) => ({
            ...prevData,
            [formId]: {
                apiCalling: true
            }
        }))
        try {
            await CustomerApi.syncUserFormResponse(customerId, formId);
            setSyncExpApiDetails((prevData) => ({
                ...prevData,
                [formId]: {
                    apiCalling: false
                }
            }))
            fetchUserUnSyncedExpDetails();
        } catch (error) {
            setSyncExpApiDetails((prevData) => ({
                ...prevData,
                [formId]: {
                    apiCalling: false,
                    error: error?.message
                }
            }))
            setTimeout(()=>{
                setSyncExpApiDetails((prevData) => ({
                    ...prevData,
                    [formId]: {
                        apiCalling: false
                    }
                }))
            },3000)
        }

    }, [customerId, fetchUserUnSyncedExpDetails])

    useEffect(() => {
        fetchUserDetails();
        fetchUserUnSyncedExpDetails();
    }, [fetchUserDetails,fetchUserUnSyncedExpDetails])



    return (
        <Container fluid className='mb-5'>
            <PageHeading>
                <Typography variant='h2'
                    style={{
                        textTransform: 'capitalize',
                        borderColor: useTheme().palette.text.primary,
                        color: useTheme().palette.text.primary,
                        fontFamily: 'gilroy-medium'
                    }}
                >
                    User Details
                </Typography>
            </PageHeading>
            <PagePanel>
                <Grid container spacing={2}>
                    {userDetails?.userResponse?.sections?.map(section => {
                        return (
                            <Grid item xs={8} key={section?.id}>
                                {section?.presentationData?.tilte && <Typography variant='h3'>{section?.presentationData?.tilte}</Typography>}
                                {section?.presentationData?.subTitle && <Typography variant='h4'>{section?.presentationData?.subTitle}</Typography>}
                                <Grid container spacing={2}>
                                    {section?.questions?.map((question) => {
                                        return (
                                            <Grid item xs={8} key={question?.id}>
                                                <Typography variant="h3">
                                                    {question?.presentationData?.title}
                                                </Typography>
                                                <Typography>
                                                    {formatAnswerForUserResponse(question)}
                                                </Typography>
                                            </Grid>
                                        )
                                    })}
                                </Grid>
                            </Grid>
                        )
                    })}
                </Grid>
                <PageHeading>
                    <Typography sx={{
                        textTransform: 'capitalize',
                        borderColor: useTheme().palette.text.primary,
                        color: useTheme().palette.text.primary,
                        fontFamily: 'gilroy-medium',
                        marginTop: 20
                    }} variant="h2" pb={siteTheme.spacing(4)}>
                        Unsynced Experience Details
                    </Typography>
                </PageHeading>
                <Grid container spacing={2} style={{padding : 20}} >
                    <Row className="PointsBalance--wrapper">
                        {unSyncedExpDetails?.map(expDetail => (
                            <Row key={expDetail?.id}>
                                <Col md={12} style={{
                                    borderBottom: '1px solid #b1b4ba',
                                    marginBottom: 15,
                                    paddingBottom: 5
                                }}>
                                    <div className='d-flex justify-content-between align-items-center'>
                                        <span>
                                            <Typography variant="detailsDataValue">
                                                {`${expDetail?.metaData?.enquiryReferenceId}`}
                                            </Typography>
                                        </span>
                                        <Button
                                            variant="contained" color="primary"
                                            onClick={() => syncExpDetails(expDetail?.id)}
                                        >
                                            {syncExpApiDetails[expDetail?.id]?.apiCalling && <Spinner
                                                as="span"
                                                animation="grow"
                                                size="sm"
                                                role="status"
                                                aria-hidden="true"
                                            />}
                                            Sync Details
                                        </Button>
                                    </div>
                                    {syncExpApiDetails[expDetail?.id]?.error && (
                                        <Alert variant="danger" style={{marginTop:20}} >Unable to sync details</Alert>
                                    )}
                                </Col>
                            </Row>
                        ))}
                        {(unSyncedExpDetails?.length === 0) && (
                            <Col className="PointsBalance--Amount">No unsynced user response</Col>
                        )}
                    </Row>
                </Grid>
            </PagePanel>
        </Container>
    )
}

export default UserFormReponses;